<script setup>
import { onMounted, reactive, ref } from 'vue';

const props = defineProps({
    fillColors: {
        type: Array,
        default: () => ['#7814d7', '#7814d7', '#7814d7', '#7814d7'],
    },
    series: {
        type: Array,
        required: false,
        default: () => [44, 55, 41, 17, 15],
    },
    labels: {
        type: Array,
        required: false,
        default: () => ['A', 'B', 'C', 'D', 'E'],
    },
});

const cseries = ref([]);

const parseColorChannels = (color) => {
    const normalizedColor = color.trim();
    const hexMatch = normalizedColor.match(/^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i);

    if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3) {
            hex = hex.split('').map((channel) => channel + channel).join('');
        }

        return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16),
        ];
    }

    const rgbMatch = normalizedColor.match(/^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/i);
    if (!rgbMatch) {
        return null;
    }

    return rgbMatch.slice(1, 4).map((channel) => Math.min(255, Number(channel)));
};

const contrastTextColor = (backgroundColor) => {
    const channels = parseColorChannels(backgroundColor);
    if (channels === null) {
        return '#ffffff';
    }

    const luminance = channels
        .map((channel) => channel / 255)
        .map((channel) => channel <= 0.04045
            ? channel / 12.92
            : Math.pow((channel + 0.055) / 1.055, 2.4))
        .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index], 0);

    return luminance > 0.179 ? '#000000' : '#ffffff';
};

const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const tooltipContent = ({ series, seriesIndex, w }) => {
    const backgroundColor = props.fillColors[seriesIndex % props.fillColors.length] ?? '#ffffff';
    const safeBackgroundColor = parseColorChannels(backgroundColor) === null ? '#ffffff' : backgroundColor;
    const textColor = contrastTextColor(safeBackgroundColor);
    const label = escapeHtml(w.globals.labels[seriesIndex]);
    const value = escapeHtml(series[seriesIndex]);

    return `<div style="background-color: ${safeBackgroundColor}; color: ${textColor}; padding: 0.5rem 0.75rem; font-weight: 600;">${label}: ${value}</div>`;
};

const options = reactive({
    labels: props.labels,
    stroke: {
        width: 0,
    },
    legend: {
        show: false,
    },
    dataLabels: {
        enabled: false,
    },
    tooltip: {
        custom: tooltipContent,
    },
    plotOptions: {
        pie: {
            expandOnClick: false,
            stroke: {
                show: false,
            },
            donut: {
                labels: {
                    show: false,
                },
                dataLabels: {
                    show: false,
                },
            },
        },
    },
    colors: props.fillColors,
});

const target = ref(null);

onMounted(() => {
    cseries.value = props.series;
});
</script>

<template>
    <div class="relative group comparison-donut-chart-container">
        <apexchart ref="target" type="donut" :options="options" :series="cseries"></apexchart>
        <div class="absolute font-semibold text-center transform -translate-x-1/2 -translate-y-1/2 pointer-events-none comparison-donut-center-label top-1/2 left-1/2">
            <slot name="label"></slot>
        </div>
    </div>
</template>

<style scoped>
.comparison-donut-chart-container {
    container-type: inline-size;
}

.comparison-donut-center-label {
    width: 42%;
    font-size: clamp(1rem, 10cqi, 4.5rem);
    line-height: 1;
}
</style>
